import { HEX_ALPHA, cyan, grey } from "@faro-lotv/flat-ui";
import type { Theme } from "@mui/material";
import { alpha } from "@mui/material";
import { type SystemStyleObject } from "@mui/system";
import { DateTime } from "luxon";
import { CellUnit } from "../range-selection";

/** Opacity of the grey hover wash on a cell or button outside the active range */
export const HOVER_OPACITY = 0.26;

/** Twice MUI's dark `primary/_states/selected` (16%), so the band reads as one with its ends */
const BAND_OPACITY = 0.32;

export const DAYS_PER_WEEK = 7;
export const OVAL_RADIUS = "999px";

// Cells fill the row with no gap, so the cells of the active range join into one band
const DAY_CELL_WIDTH = `calc(100% / ${DAYS_PER_WEEK})`;
const DAY_CELL_HEIGHT = "32px";

/** How a cell relates to the active range */
export type RangeRole = {
  /** True when the cell overlaps the active range */
  isSelected: boolean;
  /** True when the cell holds the range's first day */
  isRangeStart: boolean;
  /** True when the cell holds the range's last day */
  isRangeEnd: boolean;
};

export const OUTSIDE_RANGE: RangeRole = {
  isSelected: false,
  isRangeStart: false,
  isRangeEnd: false,
};

/**
 * @returns How the cell relates to the active range
 * @param cell Any instant in the cell
 * @param unit The unit the cell spans
 * @param start Inclusive start of the active range
 * @param end Inclusive end of the active range
 */
export function rangeRoleOf(
  cell: DateTime,
  unit: CellUnit,
  start?: Date,
  end?: Date,
): RangeRole {
  const from = cell.startOf(unit).toJSDate();
  const to = cell.endOf(unit).toJSDate();
  if (!start || !end || to < start || from > end) return OUTSIDE_RANGE;
  return {
    isSelected: true,
    isRangeStart: from <= start,
    isRangeEnd: to >= end,
  };
}

export const CALENDAR_SX = {
  width: "100%",
  height: "auto",
  maxHeight: "unset",
  m: 0,
  userSelect: "none",
  "& .MuiDayCalendar-header": { m: 0 },
  "& .MuiDayCalendar-weekContainer": { mx: 0, my: "2px" },
  "& .MuiDayCalendar-weekDayLabel": {
    color: grey[500],
    fontSize: "10px",
    width: DAY_CELL_WIDTH,
    height: "22px",
    m: 0,
  },
  // With animations reduced there is no slide, so the grid can take the height of its weeks
  "& .MuiDayCalendar-slideTransition": {
    minHeight: 0,
    overflowX: "hidden",
  },
  "& .MuiDayCalendar-slideTransition > *": { position: "static" },
  // A row's children are MUI's filler cells and the tooltip wrappers around the real days
  "& .MuiDayCalendar-weekContainer > *": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: DAY_CELL_WIDTH,
    height: DAY_CELL_HEIGHT,
    m: 0,
  },
  "& .MuiPickerDay-root:not(.MuiPickerDay-fillerCell)": {
    width: DAY_CELL_HEIGHT,
    height: DAY_CELL_HEIGHT,
    m: 0,
  },
};

/**
 * Same idle/hover pairs as flat-ui's dark primary (range ends) and ghost (the rest) buttons.
 *
 * @returns The colours of a cell for its place in the active range
 * @param role How the cell relates to the active range
 */
export function cellColors({
  isSelected,
  isRangeStart,
  isRangeEnd,
}: RangeRole): { background: string; hover: string; color: string } {
  if (isRangeStart || isRangeEnd) {
    return { background: cyan[400], hover: cyan[300], color: grey[950] };
  }
  if (isSelected) {
    return {
      background: alpha(cyan[400], BAND_OPACITY),
      hover: alpha(cyan[400], HEX_ALPHA.alpha66),
      color: grey[0],
    };
  }
  return {
    background: "transparent",
    hover: alpha(grey[500], HOVER_OPACITY),
    color: grey[100],
  };
}

/**
 * @returns The styling of a round day or oval month button. Cells between the range's ends
 * stay clear, letting the band drawn by {@link bandSx} show through.
 * @param role How the cell relates to the active range
 * @param isToday True for the current day or month
 * @param isDisabled True for a cell that cannot be picked, such as one in the future
 */
export function cellSx(
  role: RangeRole,
  isToday: boolean,
  isDisabled: boolean,
): SystemStyleObject<Theme> {
  const { background, hover, color } = cellColors(role);
  const isEndpoint = role.isRangeStart || role.isRangeEnd;

  return {
    userSelect: "none",
    // Doubled specificity, as MUI's own PickerDay rules beat a plain sx property
    "&&": {
      borderRadius: OVAL_RADIUS,
      fontWeight: 400,
      color: isDisabled ? grey[600] : color,
      backgroundColor:
        role.isSelected && !isEndpoint ? "transparent" : background,
      border: `1px solid ${isToday ? grey[700] : "transparent"}`,
      outline: "none",
      "&:hover": { backgroundColor: hover },
    },
  };
}

/**
 * @returns The styling of the wrapper around a cell, which carries the band joining the
 * range's ends: across the whole wrapper between them, from the middle out at the ends
 * @param role How the cell relates to the active range
 * @param isRowStart True for the first cell of a row, where the band gets a rounded end
 * @param isRowEnd True for the last cell of a row, where the band gets a rounded end
 */
export function bandSx(
  { isSelected, isRangeStart, isRangeEnd }: RangeRole,
  isRowStart: boolean,
  isRowEnd: boolean,
): SystemStyleObject<Theme> {
  const band =
    isSelected && !(isRangeStart && isRangeEnd)
      ? alpha(cyan[400], BAND_OPACITY)
      : "transparent";
  const from = isRangeStart ? "50%" : "0%";
  const to = isRangeEnd ? "50%" : "100%";
  const left = isRowStart && !isRangeStart ? OVAL_RADIUS : "0";
  const right = isRowEnd && !isRangeEnd ? OVAL_RADIUS : "0";

  return {
    display: "flex",
    background: `linear-gradient(to right, transparent ${from}, ${band} ${from}, ${band} ${to}, transparent ${to})`,
    borderRadius: `${left} ${right} ${right} ${left}`,
  };
}
